import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

const AddPaymentMethodPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [paymentType, setPaymentType] = useState<'card' | 'paypal'>('card');
  const [cardData, setCardData] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    name: '',
    zipCode: '',
  });
  const [paypalEmail, setPaypalEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    const token = localStorage.getItem('token');
    try {
      const body = paymentType === 'card' 
        ? { type: 'card', ...cardData }
        : { type: 'paypal', email: paypalEmail };
      
      const res = await fetch(`\${API_BASE_URL}/api/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard/settings/payment-methods');
        }, 1500);
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to add payment method');
      }
    } catch (err) {
      setError('Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/.{1,4}/g);
    return match ? match.join(' ') : cleaned;
  };

  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings/payment-methods')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Payment Methods
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Add Payment Method</h1>
      <p className="mb-6 text-sm text-gray-600">
        Add a new payment method for your subscriptions
      </p>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Payment Type Selector */}
        <div className="flex space-x-4 mb-6">
          <button
            type="button"
            onClick={() => setPaymentType('card')}
            className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
              paymentType === 'card'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            Credit/Debit Card
          </button>
          <button
            type="button"
            onClick={() => setPaymentType('paypal')}
            className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
              paymentType === 'paypal'
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            PayPal
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {paymentType === 'card' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Card Number
                </label>
                <input
                  type="text"
                  value={cardData.cardNumber}
                  onChange={(e) =>
                    setCardData({ ...cardData, cardNumber: formatCardNumber(e.target.value) })
                  }
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="text"
                    value={cardData.expiry}
                    onChange={(e) =>
                      setCardData({ ...cardData, expiry: formatExpiry(e.target.value) })
                    }
                    placeholder="MM/YY"
                    maxLength={5}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                  <input
                    type="text"
                    value={cardData.cvv}
                    onChange={(e) =>
                      setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })
                    }
                    placeholder="123"
                    maxLength={4}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cardholder Name
                </label>
                <input
                  type="text"
                  value={cardData.name}
                  onChange={(e) => setCardData({ ...cardData, name: e.target.value })}
                  placeholder="John Doe"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing ZIP Code
                </label>
                <input
                  type="text"
                  value={cardData.zipCode}
                  onChange={(e) => setCardData({ ...cardData, zipCode: e.target.value })}
                  placeholder="12345"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PayPal Email
              </label>
              <input
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
              <p className="mt-2 text-xs text-gray-500">
                You'll be redirected to PayPal to complete the connection
              </p>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</div>
          )}

          {success && (
            <div className="flex items-center p-3 bg-green-50 rounded-md">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm text-green-800">Payment method added!</span>
            </div>
          )}

          <div className="flex items-center space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/settings/payment-methods')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Payment Method'}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-start">
            <CreditCardIcon className="w-5 h-5 text-gray-400 mt-0.5 mr-2" />
            <div className="text-xs text-gray-500">
              Your payment information is encrypted and securely processed by Stripe. We never store your full card details.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentMethodPage;
